// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Button,
    Dropdown,
    Menu,
    MenuItem,
} from '@plesk/ui-library';
import Action from 'common/components/Action/Action';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { StyledActions } from 'common/components/Actions/Styles';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import MenuItemWithConfirmation from 'common/components/MenuItemWithConfirmation';
import { useIsUp } from 'common/hooks/useBreakpoint';
import { BREAKPOINTS } from 'common/styles/breakpoints';
import { ReactComponent as ThreeVerticalDots } from 'common/icons/three-vertical-dots.svg';
import { COLORS } from 'common/theme';

export interface IActionsCellConfirmation {
    button: React.ReactNode;
    title?: React.ReactNode;
    text?: React.ReactNode;
    tooltip?: React.ReactNode;
    handleConfirm: () => void | Promise<void>;
    isLoading?: boolean;
}

export interface IActionsCellAction {
    icon: string | React.ReactElement;
    label: React.ReactNode;
    onClick?: () => void;
    tooltip?: React.ReactNode;
    disabled?: boolean;
    confirmation?: IActionsCellConfirmation;
    'data-cy'?: string;
    key?: React.Key;
}

export interface IActionsCellProps {
    actions: IActionsCellAction[];
    breakpoint?: number;
    triggerTooltip?: React.ReactNode;
    'data-cy'?: string;
}

export const ActionsCell: React.FC<IActionsCellProps> = ({
    actions,
    breakpoint = BREAKPOINTS.LG,
    triggerTooltip,
    'data-cy': dataCy,
}) => {
    const isInline = useIsUp(breakpoint);

    if (isInline) {
        return (
            <StyledActions data-cy={dataCy}>
                {actions.map((action, index) => {
                    const key = action.key ?? index;

                    if (action.confirmation) {
                        return (
                            <ButtonWithConfirmation
                                key={key}
                                disabled={action.disabled}
                                isLoading={action.confirmation.isLoading}
                                translations={{
                                    button: action.confirmation.button,
                                    title: action.confirmation.title,
                                    text: action.confirmation.text,
                                    tooltip: action.confirmation.tooltip ?? action.tooltip,
                                }}
                                handleConfirm={action.confirmation.handleConfirm}
                                data-cy={action['data-cy']}
                                icon={action.icon}
                            />
                        );
                    }

                    const actionEl = (
                        <Action
                            icon={action.icon}
                            className="action-icon"
                            onClick={action.onClick}
                            disabled={action.disabled}
                            data-cy={action['data-cy']}
                        />
                    );

                    return action.tooltip ? (
                        <Tooltip key={key} title={action.tooltip}>
                            {actionEl}
                        </Tooltip>
                    ) : (
                        <React.Fragment key={key}>{actionEl}</React.Fragment>
                    );
                })}
            </StyledActions>
        );
    }

    return (
        <Dropdown
            data-cy={dataCy}
            menu={(
                <Menu>
                    {actions.map((action, index) => {
                        const key = action.key ?? index;

                        if (action.confirmation) {
                            return (
                                <MenuItemWithConfirmation
                                    key={key}
                                    isLoading={action.confirmation.isLoading}
                                    icon={action.icon}
                                    translations={{
                                        button: action.confirmation.button,
                                        title: action.confirmation.title,
                                        text: action.confirmation.text,
                                    }}
                                    menuItemText={action.label}
                                    handleConfirm={action.confirmation.handleConfirm}
                                    dataCy={action['data-cy']}
                                    disabled={action.disabled}
                                />
                            );
                        }

                        return (
                            <MenuItem
                                key={key}
                                icon={action.icon}
                                onClick={action.onClick}
                                disabled={action.disabled}
                                data-cy={action['data-cy']}
                            >
                                {action.label}
                            </MenuItem>
                        );
                    })}
                </Menu>
            )}
            menuPlacement="bottom-end"
        >
            <Button
                ghost={true}
                icon={<ThreeVerticalDots style={{ width: 16, height: 16, color: COLORS.GREY_80 }} />}
                tooltip={triggerTooltip}
            />
        </Dropdown>
    );
};

